/**
 * "How they found us": the contact's credited traffic source, the first and
 * last place they came from, their website visits, the referral code they
 * used, and every result we sent back to the ad platforms for them.
 * Data: GET /api/contacts/:id → attribution, source, touches, website, conversions.
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Globe, Megaphone, MousePointerClick, Route, Send, UserRoundPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { dotClass, timeAgo, usd } from "@/features/command/ui";

export interface Attribution {
  model: "manual" | "affiliate" | "last_touch" | "first_touch" | "platform" | "direct";
  note?: string | null;
  source_id: string | null;
  source_name: string | null;
  signed_up_at: string | null;
  first: TouchSummary | null;
  last: TouchSummary | null;
  affiliate: { id: string; name: string; kind: string; code: string | null } | null;
  touches: number;
  visits: number;
  click_ids: Record<string, string>;
}
interface TouchSummary {
  ts: string; source_name: string | null; how: string; utm_source?: string | null; utm_medium?: string | null;
  utm_campaign?: string | null; referrer?: string | null; landing_page?: string | null;
}
export interface Touch {
  ts: string; kind: string; utm_source: string | null; utm_medium: string | null; utm_campaign: string | null;
  utm_content: string | null; referrer: string | null; landing_page: string | null; click_ids: Record<string, string>;
}
export interface Website {
  visits: number; pageviews: number; first_seen: string | null; last_seen: string | null;
  recent: Array<{ ts: string; path: string; source: string; channel: string; device: string | null; country: string | null }> | null;
}
export interface ConversionSend {
  event: string; status: string; detail: string | null; value_usd: string | null; matched_on: string[]; created_at: string;
  platform: string | null; destination_name: string | null;
}

const MODEL_WORDS: Record<Attribution["model"], string> = {
  manual: "set on purpose",
  affiliate: "signed up with a referral code",
  last_touch: "the last place they came from before signing up",
  first_touch: "the first place they came from",
  platform: "how the platform says they signed up",
  direct: "no source was recorded",
};
const EVENT_WORDS: Record<string, string> = { signup: "Signup", trial: "Trial start", purchase: "Payment" };
const CLICK_WORDS: Record<string, string> = {
  fbclid: "Facebook/Instagram click", gclid: "Google ad click", gbraid: "Google ad click", wbraid: "Google ad click",
  ttclid: "TikTok ad click", msclkid: "Microsoft ad click", li_fat_id: "LinkedIn ad click", rdt_cid: "Reddit ad click",
};

function host(u: string | null | undefined): string | null {
  if (!u) return null;
  try { return new URL(/^https?:/.test(u) ? u : `https://${u}`).hostname.replace(/^www\./, ""); } catch { return u; }
}
function cleanPath(p: string | null | undefined): string {
  if (!p) return "/";
  return p.split("?")[0] || "/";
}

export function FoundUsPanel({
  attribution, source, touches, website, conversions,
}: {
  attribution: Attribution | null;
  source: { id: string; slug: string; name: string; color: string | null } | null;
  touches: Touch[];
  website: Website | null;
  conversions: ConversionSend[];
}) {
  const location = useLocation();
  const [showAll, setShowAll] = useState(false);
  const a = attribution;
  const clickKinds = [...new Set(Object.keys(a?.click_ids ?? {}).map((k) => CLICK_WORDS[k] ?? k))];
  const recent = website?.recent ?? [];

  return (
    <div className="cc-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Route className="h-4 w-4 text-indigo-300" />
        <h3 className="text-sm font-semibold text-zinc-100">How they found us</h3>
      </div>

      {/* The credit */}
      <div className="rounded-xl border border-indigo-400/25 bg-indigo-500/10 p-3">
        <div className="text-[11px] font-medium uppercase tracking-wider text-indigo-200/80">Credited to</div>
        {source ? (
          <Link to={`/traffic/${source.slug}`} state={{ from: location.pathname }} className="mt-1 inline-flex items-center gap-2 text-base font-semibold text-zinc-50 hover:underline">
            <span className={cn("h-2 w-2 rounded-full", dotClass(source.color))} />
            {source.name}
          </Link>
        ) : (
          <div className="mt-1 text-base font-semibold text-zinc-300">Not credited yet</div>
        )}
        {a && (a.model === "manual" && a.note
          ? <p className="mt-1 text-xs text-indigo-100/70">{a.note}</p>
          : <p className="mt-1 text-xs text-indigo-100/70">Because it's {MODEL_WORDS[a.model]}.</p>)}
        {a?.affiliate && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-200">
            <UserRoundPlus className="h-3.5 w-3.5" />
            Referred by{" "}
            {a.affiliate.id ? (
              <Link to={`/affiliates/${a.affiliate.id}`} state={{ from: location.pathname }} className="font-semibold hover:underline">{a.affiliate.name}</Link>
            ) : (
              <span className="font-semibold">code "{a.affiliate.code}"</span>
            )}
          </div>
        )}
      </div>

      {/* First and last touch */}
      {(a?.first || a?.last) && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          {a?.first && <TouchCard label="First came from" t={a.first} />}
          {a?.last && (a.last.ts !== a.first?.ts) && <TouchCard label="Last before signing up" t={a.last} />}
        </div>
      )}

      {/* Signals */}
      <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
        {website && website.visits > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-zinc-800/80 px-2 py-0.5 text-zinc-300"><Globe className="h-3 w-3" />{website.visits} visit{website.visits === 1 ? "" : "s"}, {website.pageviews} pages</span>
        )}
        {clickKinds.map((k) => (
          <span key={k} className="inline-flex items-center gap-1 rounded-full bg-zinc-800/80 px-2 py-0.5 text-zinc-300"><MousePointerClick className="h-3 w-3" />{k}</span>
        ))}
        {a && a.touches > 0 && <span className="rounded-full bg-zinc-800/80 px-2 py-0.5 text-zinc-300">{a.touches} tracked arrival{a.touches === 1 ? "" : "s"}</span>}
        {!website?.visits && !clickKinds.length && !a?.touches && (
          <span className="text-zinc-500">No browsing history linked yet. New signups on syncsuite.co get it automatically.</span>
        )}
      </div>

      {/* Pages they read */}
      {recent.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowAll((v) => !v)} className="flex w-full items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-300">
            What they looked at
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showAll && "rotate-180")} />
          </button>
          <ul className="mt-1.5 space-y-1">
            {(showAll ? recent : recent.slice(0, 4)).map((p, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-xs">
                <span className="min-w-0 truncate text-zinc-300" title={p.path}>{cleanPath(p.path)}</span>
                <span className="shrink-0 text-zinc-500">{timeAgo(p.ts)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Every arrival we recorded */}
      {showAll && touches.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Arrivals</div>
          <ul className="mt-1.5 space-y-1">
            {touches.map((t, i) => (
              <li key={i} className="text-xs text-zinc-400">
                <span className="text-zinc-300">{new Date(t.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>{" "}
                {[t.utm_source, t.utm_medium, t.utm_campaign].filter(Boolean).join(" / ") || host(t.referrer) || "direct"}
                {Object.keys(t.click_ids ?? {}).length > 0 && <span className="text-zinc-500"> · {Object.keys(t.click_ids).join(", ")}</span>}
                {t.kind === "signup" && <span className="text-indigo-300"> · signup</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sent back to the ad platforms */}
      {conversions.length > 0 && (
        <div className="mt-3 border-t border-zinc-800/60 pt-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500"><Send className="h-3 w-3" /> Sent to ad platforms</div>
          <ul className="space-y-1">
            {conversions.map((c, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-xs">
                <span className="min-w-0 truncate text-zinc-300">
                  <Megaphone className="mr-1 inline h-3 w-3 text-zinc-500" />
                  {EVENT_WORDS[c.event] ?? c.event}{c.value_usd ? ` ${usd(Number(c.value_usd), { cents: true })}` : ""} → {c.destination_name || c.platform}
                </span>
                <span className={cn("shrink-0", c.status === "sent" ? "text-emerald-300" : c.status === "failed" ? "text-rose-300" : "text-zinc-500")} title={c.detail ?? ""}>
                  {c.status === "sent" ? "sent" : c.status === "failed" ? "failed" : c.status === "skipped" ? "skipped" : c.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TouchCard({ label, t }: { label: string; t: TouchSummary }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-zinc-100">{t.source_name ?? host(t.referrer) ?? "Unknown"}</div>
      <div className="mt-0.5 text-[11px] leading-snug text-zinc-400">{t.how}</div>
      <div className="mt-1 text-[11px] text-zinc-500">
        {new Date(t.ts).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
        {t.landing_page ? <> · landed on {cleanPath(t.landing_page)}</> : null}
      </div>
    </div>
  );
}
